"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";

export const FILTER_SEPARATOR = "_";

interface UseFilterOptionProps {
	code: string;
	value: string;
}

export function useFilterOption({ code, value }: UseFilterOptionProps) {
	const searchParams = useSearchParams();
	const pathname = usePathname();

	const activeOptions = useMemo(
		() => searchParams?.get(code)?.split(FILTER_SEPARATOR) ?? [],
		[code, searchParams],
	);

	const isActive = useMemo(() => activeOptions.includes(value), [activeOptions, value]);

	const href = useMemo(() => {
		const entries = searchParams?.entries() || [];
		// remove the current aggregation code from the search params
		const searchParamEntries = Object.fromEntries([...entries].filter(([key]) => key !== code));

		// if the option is active, remove it from the search params otherwise add it
		const queryParams = isActive
			? activeOptions.filter((option) => option !== value)
			: [...activeOptions, value];

		// sort the query params to make sure the order is always the same
		queryParams.sort();

		const query =
			queryParams.length === 0
				? // if there are no query params, remove the attribute code from the search params
					searchParamEntries
				: // otherwise add the attribute code to the search params
					{
						...searchParamEntries,
						[code]: queryParams.join(FILTER_SEPARATOR),
					};

		return { pathname, query } as const;
	}, [searchParams, isActive, activeOptions, value, code, pathname]);

	return {
		isActive,
		href,
		activeOptions,
	} as const;
}
